import { formatDistanceToNow, format } from "date-fns";
import Markdown from "@/components/Markdown";
import {
  Plus, MessageSquare, Lock, ArrowRightLeft, ArrowUpRight, CheckCircle2,
  Circle, Eye, Clock, Star, Tag, Zap,
} from "lucide-react";

const EVENT_ICON = {
  ticket_created: Plus,
  customer_reply: MessageSquare,
  staff_reply: MessageSquare,
  internal_note: Lock,
  ticket_assigned: ArrowRightLeft,
  status_changed: CheckCircle2,
  priority_updated: Zap,
  department_changed: ArrowRightLeft,
  ticket_viewed: Eye,
  ticket_rated: Star,
};

const EVENT_TONE = {
  ticket_created: "bg-primary/15 text-primary border-primary/30",
  customer_reply: "bg-secondary text-foreground border-border",
  staff_reply: "bg-primary/10 text-primary border-primary/25",
  internal_note: "bg-accent/10 text-accent border-accent/30",
  ticket_assigned: "bg-secondary text-foreground border-border",
  status_changed: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  priority_updated: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  ticket_rated: "bg-primary/10 text-primary border-primary/30",
  ticket_viewed: "bg-muted text-muted-foreground border-border",
};

const REPLY_EVENTS = new Set(["customer_reply", "staff_reply", "internal_note"]);

export default function TicketTimeline({ events }) {
  return (
    <ol className="relative border-l border-border/70 ml-4 space-y-6" data-testid="ticket-timeline">
      {events.map((e) => {
        const Icon = EVENT_ICON[e.event_type] || Circle;
        const tone = EVENT_TONE[e.event_type] || EVENT_TONE.ticket_viewed;
        const isReply = REPLY_EVENTS.has(e.event_type);
        return (
          <li key={e.event_id} className="ml-6 relative">
            <span className={`absolute -left-[34px] top-1 h-7 w-7 rounded-full border ${tone} flex items-center justify-center`}>
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                {e.event_type.replace(/_/g, " ")}
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs">
                <span className="font-medium">{e.actor_name || "System"}</span>
                <span className="text-muted-foreground"> ({e.actor_role || "system"})</span>
              </span>
              {e.meta?.internal && (
                <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-accent/40 text-accent">Internal</span>
              )}
              <span className="ml-auto text-xs font-mono tracking-tight text-muted-foreground" title={format(new Date(e.created_at), "PPpp")}>
                {formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}
              </span>
            </div>
            {isReply && e.meta?.body && (
              <div className={`mt-2 rounded-lg p-3 border text-sm leading-relaxed ${e.meta.internal ? "bg-accent/5 border-accent/20" : "bg-card border-border/60"}`}>
                <Markdown>{e.meta.body}</Markdown>
              </div>
            )}
            {!isReply && e.meta && Object.keys(e.meta).length > 0 && (
              <div className="mt-1.5 text-xs text-muted-foreground">
                {e.meta.from && e.meta.to && <>from <span className="font-mono">{e.meta.from}</span> → <span className="font-mono text-foreground">{e.meta.to}</span></>}
                {e.meta.to && !e.meta.from && <>→ <span className="font-mono text-foreground">{e.meta.to}</span></>}
                {e.meta.rating && <>rated <span className="font-mono text-foreground">{e.meta.rating}/5</span></>}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
